// Рисование плаката 3×3 на canvas. Одна функция для предпросмотра и для файла.

import { N } from './state.js';
import { imageFor } from './images.js';

export const W = 1080;
export const H = 1620;
const MARGIN = 60;
const GAP = 28;
const TOP = 210;
const FOOT = 84;
const COLS = 3;
export const TILE_W = (W - MARGIN * 2 - GAP * (COLS - 1)) / COLS;
export const TILE_H = (H - TOP - FOOT - GAP * 2) / 3;
const R = 12;
const PLATE_H = 92;   // плашка с подписью на плитке
const PLATE_M = 12;

const THEMES = {
  dark: { bg: '#0e1210', ink: '#f2ead8', muted: '#b3aa92', line: '#34403a', empty: '#1a221c', accent: '#e8a33d', plate: '#f4efe6', plateInk: '#1c1714', plateMuted: '#5e5546' },
  light: { bg: '#f4efe6', ink: '#1c1714', muted: '#5e5546', line: '#cbbfa9', empty: '#ebe3d3', accent: '#b9761b', plate: '#1c1714', plateInk: '#f2ead8', plateMuted: '#b3aa92' },
};
// Плитки без картинки: глубокие тона, на которых читается светлый текст.
const TONES = ['#2f4a3a', '#5b2a2a', '#3a3f5c', '#5a4630', '#2d4b52', '#4c3358', '#59523a', '#3f3f3f', '#6b3b2a'];

const HD = '"Lora", Georgia, serif';
const BD = '"Manrope", system-ui, sans-serif';

export function tileRect(i) {
  const col = i % COLS;
  const row = Math.floor(i / COLS);
  return { x: MARGIN + col * (TILE_W + GAP), y: TOP + row * (TILE_H + GAP), w: TILE_W, h: TILE_H };
}

// Номер ячейки под точкой холста (в координатах W×H) или -1.
export function hitTile(x, y) {
  for (let i = 0; i < N; i++) {
    const r = tileRect(i);
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return i;
  }
  return -1;
}

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Перенос по словам: не больше maxLines строк, хвост — «…». Слишком длинное слово режем.
function wrap(ctx, text, maxW, maxLines) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  const fit = (s) => ctx.measureText(s).width <= maxW;
  for (let w of words) {
    while (!fit(w) && w.length > 1) {
      let k = w.length - 1;
      while (k > 1 && !fit(w.slice(0, k))) k--;
      if (line) { lines.push(line); line = ''; }
      lines.push(w.slice(0, k));
      w = w.slice(k);
    }
    const t = line ? line + ' ' + w : w;
    if (fit(t)) line = t;
    else { lines.push(line); line = w; }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const cut = lines.slice(0, maxLines);
    let last = cut[maxLines - 1];
    while (last.length > 1 && !fit(last + '…')) last = last.slice(0, -1);
    cut[maxLines - 1] = last.replace(/[\s,.;:—-]+$/, '') + '…';
    return cut;
  }
  return lines;
}

const hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };

function coverFit(ctx, img, x, y, w, h) {
  const k = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw = w / k;
  const sh = h / k;
  ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, x, y, w, h);
}

function drawTitle(ctx, t, text) {
  let size = 72;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = t.ink;
  const maxW = W - MARGIN * 2;
  do { ctx.font = `700 ${size}px ${HD}`; size -= 2; } while (ctx.measureText(text).width > maxW && size > 34);
  const lines = wrap(ctx, text, maxW, 2);
  const lh = size * 1.15;
  const baseY = 112 - (lines.length - 1) * lh * 0.5;
  lines.forEach((l, i) => ctx.fillText(l, W / 2, baseY + i * lh));
  ctx.fillStyle = t.accent;
  ctx.fillRect(W / 2 - 36, TOP - 52, 72, 4);
}

function drawTile(ctx, t, cell, i, opts) {
  const { x, y, w, h } = tileRect(i);
  const has = cell.title || cell.sub || cell.img;
  ctx.save();
  rr(ctx, x, y, w, h, R);
  ctx.clip();
  if (!has) {
    ctx.fillStyle = t.empty;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
    ctx.save();
    ctx.setLineDash([10, 8]);
    ctx.strokeStyle = t.line;
    ctx.lineWidth = 2;
    rr(ctx, x + 1, y + 1, w - 2, h - 2, R);
    ctx.stroke();
    ctx.restore();
    if (opts.hints) {
      ctx.fillStyle = t.muted;
      ctx.font = `600 24px ${BD}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Выбрать', x + w / 2, y + h / 2);
    }
    return;
  }
  const e = cell.img ? imageFor(cell.img) : null;
  const img = e && e.ready ? e.img : null;
  if (img) {
    coverFit(ctx, img, x, y, w, h);
  } else {
    // Нет картинки: цветная плитка с монограммой, название и автор — на плашке ниже.
    ctx.fillStyle = TONES[hash(cell.title + cell.sub) % TONES.length];
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = 'rgba(242,234,216,0.22)';
    ctx.lineWidth = 2;
    rr(ctx, x + 14, y + 14, w - 28, h - 28, R - 4);
    ctx.stroke();
    const letter = (cell.title || cell.sub).trim().charAt(0).toUpperCase();
    ctx.fillStyle = 'rgba(242,234,216,0.88)';
    ctx.font = `700 150px ${HD}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(letter, x + w / 2, y + (h - PLATE_H - PLATE_M) / 2);
  }
  // Подпись: плашка у нижнего края каждой плитки (название и автор или подпись).
  const px = x + PLATE_M;
  const pw = w - PLATE_M * 2;
  const py = y + h - PLATE_M - PLATE_H;
  ctx.fillStyle = t.plate;
  rr(ctx, px, py, pw, PLATE_H, R - 2);
  ctx.fill();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 24px ${HD}`;
  const tl = wrap(ctx, cell.title || cell.sub, pw - 28, cell.sub && cell.title ? 2 : 3);
  ctx.font = `600 17px ${BD}`;
  const sl = cell.title && cell.sub ? wrap(ctx, cell.sub, pw - 28, 1) : [];
  const block = tl.length * 28 + (sl.length ? 24 : 0);
  let ty = py + (PLATE_H - block) / 2 + 22;
  ctx.fillStyle = t.plateInk;
  ctx.font = `700 24px ${HD}`;
  for (const l of tl) { ctx.fillText(l, px + 14, ty); ty += 28; }
  if (sl.length) {
    ctx.fillStyle = t.plateMuted;
    ctx.font = `600 17px ${BD}`;
    ctx.fillText(sl[0], px + 14, ty - 2);
  }
  ctx.restore();
}

// opts: { theme, selected (-1 нет), hints }
export function renderPoster(ctx, doc, opts = {}) {
  const t = THEMES[opts.theme] || THEMES.dark;
  ctx.save();
  ctx.fillStyle = t.bg;
  ctx.fillRect(0, 0, W, H);
  drawTitle(ctx, t, doc.title || ' ');
  for (let i = 0; i < N; i++) drawTile(ctx, t, doc.cells[i], i, opts);
  if (opts.selected >= 0) {
    const { x, y, w, h } = tileRect(opts.selected);
    ctx.strokeStyle = t.accent;
    ctx.lineWidth = 6;
    rr(ctx, x - 3, y - 3, w + 6, h + 6, R + 3);
    ctx.stroke();
  }
  ctx.fillStyle = t.muted;
  ctx.font = `600 22px ${BD}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('pierogi.ru/9-knig', W / 2, H - 34);
  ctx.restore();
}

export async function fontsReady() {
  if (!document.fonts || !document.fonts.load) return;
  const sample = 'АБВгдеЁё Abc 0123';
  try {
    await Promise.all([
      document.fonts.load(`700 40px ${HD}`, sample), document.fonts.load(`700 150px ${HD}`, sample),
      document.fonts.load(`600 17px ${BD}`, sample), document.fonts.load(`600 22px ${BD}`, sample),
    ]);
  } catch { /* рисуем запасным шрифтом */ }
}
